import { array, option } from 'fp-ts';
import { set } from 'lodash/fp';
import { NewRelicEventType, NewRelicResourceType } from '@mews/enums';
import { Optional } from '@mews/std';
import { MemoryEvent } from '../memoryMonitoring';
import { NewRelicEvent } from './newRelicReporterService';

type ConnectionType = 'bluetooth' | 'cellular' | 'ethernet' | 'mixed' | 'none' | 'other' | 'unknown' | 'wifi' | 'wimax';

type EffectiveConnectionType = 'slow-2g' | '2g' | '3g' | '4g';

// Possibly undefined values are also those not supported in all browsers even when they support networkInformation API
export interface NetworkInformation {
    readonly downlink: number;
    readonly downlinkMax?: number;
    readonly effectiveType: EffectiveConnectionType;
    readonly rtt: number;
    readonly saveData?: boolean;
    readonly type?: ConnectionType;
    onchange?: EventListener;
    // Same as onchange, but some browsers use this name instead
    ontypechange?: EventListener;
}

export interface TaskAttributionTiming extends PerformanceEntry {
    readonly containerType: string;
    readonly containerSrc: string;
    readonly containerId: string;
    readonly containerName: string;
}

export interface PerformanceLongTaskTiming extends PerformanceEntry {
    readonly attribution: TaskAttributionTiming[];
}

export const isPerformanceResourceTiming = (entry: PerformanceEntry): entry is PerformanceResourceTiming =>
    entry.entryType === 'resource';
export const isPerformanceLongTaskTiming = (entry: PerformanceEntry): entry is PerformanceLongTaskTiming =>
    entry.entryType === 'longtask';

export const getNewRelicMemoryEvent: (event: MemoryEvent) => NewRelicEvent<NewRelicEventType.MemoryInfo> = set(
    'eventType',
    NewRelicEventType.MemoryInfo
);

export const getEventFromLongTaskTiming = (longTaskTiming: PerformanceLongTaskTiming) => {
    const { name, startTime, duration, attribution } = longTaskTiming;

    let newRelicEvent = {
        eventType: NewRelicEventType.LongTask,
        name,
        startTime,
        duration,
    };

    attribution.forEach((taskAttributionTiming, idx) => {
        const {
            name: attributionName,
            containerType,
            containerName,
            containerId,
            containerSrc,
        } = taskAttributionTiming;

        newRelicEvent = {
            ...newRelicEvent,
            [`attribution${idx}_name`]: attributionName,
            [`attribution${idx}_containerType`]: containerType,
            [`attribution${idx}_containerName`]: containerName,
            [`attribution${idx}_containerId`]: containerId,
            [`attribution${idx}_containerSrc`]: containerSrc,
        };
    });

    return newRelicEvent;
};

interface WithNetworkInformation {
    networkInformation: Optional<NetworkInformation>;
}

const getEventFromResourceTiming = (
    timing: PerformanceResourceTiming,
    { networkInformation }: WithNetworkInformation
) => {
    // We can't use rest syntax because it results in an empty object.
    const {
        name,
        duration,
        startTime,
        connectEnd,
        connectStart,
        decodedBodySize,
        domainLookupEnd,
        domainLookupStart,
        encodedBodySize,
        fetchStart,
        initiatorType,
        nextHopProtocol,
        redirectEnd,
        redirectStart,
        requestStart,
        responseEnd,
        responseStart,
        secureConnectionStart,
        transferSize,
        workerStart,
    } = timing;

    const { downlink, effectiveType, rtt } = networkInformation || {};

    const timingEventData = {
        eventType: NewRelicEventType.Resource,
        resourceType: NewRelicResourceType.PlatformApi,
        name,
        duration,
        startTime,
        connectEnd,
        connectStart,
        decodedBodySize,
        domainLookupEnd,
        domainLookupStart,
        encodedBodySize,
        fetchStart,
        initiatorType,
        nextHopProtocol,
        redirectEnd,
        redirectStart,
        requestStart,
        responseEnd,
        responseStart,
        secureConnectionStart,
        transferSize,
        workerStart,
        ...(downlink && effectiveType && rtt ? { downlink, effectiveType, rtt } : {}),
    };

    return timingEventData;
};

const getNewRelicPerformanceEvent =
    ({ networkInformation }: WithNetworkInformation) =>
    (entry: PerformanceEntry) => {
        if (isPerformanceLongTaskTiming(entry)) {
            return option.some(getEventFromLongTaskTiming(entry));
        }

        if (isPerformanceResourceTiming(entry)) {
            return option.some(getEventFromResourceTiming(entry, { networkInformation }));
        }

        return option.none;
    };

export const getNewRelicPerformanceEvents = (
    entryList: PerformanceEntryList,
    { networkInformation }: WithNetworkInformation
) => array.filterMap(getNewRelicPerformanceEvent({ networkInformation }))(entryList);
